import React from 'react';

const BotOverview = () => {
  return (
    <>
      {/* Section 1: Overview */}
      <div className="p-6 bg-gradient-to-br from-blue-500 to-indigo-700 text-white rounded-lg shadow-lg mb-6">
        <div className="flex items-center space-x-4 mb-6">
          <h2 className="text-3xl font-bold">The Quantum Flo Auto Algo: How It Works in Real Time</h2>
        </div>
        <p className="text-lg font-semibold mb-4">
          Institutional Logic. Retail Execution. Full Automation.
        </p>
        <p className="text-lg">
          The Quantum Flo Auto Algo is designed for active traders seeking a fully automated options strategy that removes emotion, reduces macro risk, and executes high-probability directional trades using the same technology our hedge fund team has employed for over a decade. Built on machine learning, options flow analysis, and herd behavior modeling, this system is engineered to trade SPY, SPX, and QQQ with precision and speed in your brokerage account, on your terms.
        </p>
      </div>

      {/* Section 2: How & When the Algo Trades */}
      <div className="p-6 bg-gradient-to-br from-blue-500 to-indigo-700 text-white rounded-lg shadow-lg mb-6">
        <div className="flex items-center space-x-4 mb-6">
          <h2 className="text-3xl font-bold">How & When the Algo Trades</h2>
        </div>
        <p className="text-lg">
          The algorithm is designed to execute trades based on <strong className="text-white">real-time market conditions</strong>, not preset times. It continually analyzes liquidity, sentiment, dealer positioning, volatility, and price action to generate <strong className="text-white">directional call or put entries</strong> with a high statistical edge.
        </p>
        <div className="mt-6">
          <h3 className="text-xl font-bold text-white">Entry Logic:</h3>
          <ul className="list-disc pl-6 text-lg">
            <li>Trades are typically entered <strong className="text-white">between 9:45 AM and 2:30 PM ET</strong>, after early volatility subsides.</li>
            <li>Entry is triggered when the market reaches a <strong className="text-white">key level within the predicted range</strong>, supported by options data and herd positioning.</li>
          </ul>
        </div>
        <div className="mt-6">
          <h3 className="text-xl font-bold text-white">Exit Logic:</h3>
          <ul className="list-disc pl-6 text-lg">
            <li>Most trades are closed <strong className="text-white">within 1–2 days</strong>, often the <strong className="text-white">same day</strong>.</li>
            <li>Positions are exited when price reaches the upper/lower bounds of the forecasted range, or when <strong className="text-white">herd momentum dissipates</strong>.</li>
            <li>No positions are held over the weekend, reducing gap risk.</li>
          </ul>
        </div>
      </div>

      {/* Section 3: Cash, Margin & Broker Requirements */}
      <div className="p-6 bg-gradient-to-br from-blue-500 to-indigo-700 text-white rounded-lg shadow-lg mb-6">
        <div className="flex items-center space-x-4 mb-6">
          <h2 className="text-3xl font-bold">Cash, Margin & Broker Requirements</h2>
        </div>

        <h3 className="text-xl font-bold text-white mb-4">Do I Need a Margin Account?</h3>
        <p className="text-lg">
          No. The Quantum Flo Auto Algo only buys Puts and Calls, which are <strong className="text-white">cash-secured trades</strong>. That means:
        </p>
        <ul className="list-disc pl-6 text-lg mb-4">
          <li><strong className="text-white">No margin is used.</strong></li>
          <li>You may use a cash or margin account, but margin is not required.</li>
          <li>The strategy is fully compatible with <strong className="text-white">Pattern Day Trader (PDT)</strong> rules, particularly for single-leg trades.</li>
        </ul>

        <h3 className="text-xl font-bold text-white mb-4">PDT Compliance</h3>
        <ul className="list-disc pl-6 text-lg">
          <li><strong className="text-white">Under $25K?</strong> The system will execute <strong className="text-white">single-leg trades only</strong> (one Call or one Put), keeping you within PDT rules.</li>
          <li><strong className="text-white">Over $25K?</strong> You gain access to <strong className="text-white">spread-based strategies</strong> with defined risk and more complex structures.</li>
        </ul>
      </div>

      {/* Section 4: Supported Brokers */}
      <div className="p-6 bg-gradient-to-br from-blue-500 to-indigo-700 text-white rounded-lg shadow-lg mb-6">
        <div className="flex items-center space-x-4 mb-6">
          <h2 className="text-3xl font-bold">Supported Brokers</h2>
        </div>
        <p className="text-lg">
          The Quantum Flo Auto Algo currently integrates with the following brokerage platforms:
        </p>
        <ul className="list-disc pl-6 text-lg mb-4">
          <li><strong className="text-white">Tradier</strong> (Level 3) JTNC's partner and preferred broker</li>
          <li><strong className="text-white">TastyTrade</strong> (coming soon)</li>
          
        </ul>
        <p className="text-lg">
          Without the required options trading level enabled, the system will not be able to trade. You must ensure your account is set up correctly before activating the algorithm.
        </p>
      </div>

      {/* Section 5: Risk & Execution Management */}
      <div className="p-6 bg-gradient-to-br from-blue-500 to-indigo-700 text-white rounded-lg shadow-lg mb-6">
        <div className="flex items-center space-x-4 mb-6">
          <h2 className="text-3xl font-bold">Risk & Execution Management</h2>
        </div>
        <ul className="list-disc pl-6 text-lg mb-4">
          <li>You set your <strong className="text-white">contract or spread limits per symbol</strong> based on your subscription Tier.</li>
          <li>The algo will <strong className="text-white">never exceed the number of trades</strong> you've authorized per day or per symbol.</li>
          <li>All trades are executed in <strong className="text-white">SPY, SPX, or QQQ only</strong>.</li>
          <li>As additional symbols and asset classes (like futures and crypto) are added, <strong className="text-white">risk diversification will expand</strong>, offering broader exposure and reduced single-symbol risk.</li>
        </ul>
      </div>

      {/* Section 6: What Makes This Algo Different */}
      <div className="p-6 bg-gradient-to-br from-blue-500 to-indigo-700 text-white rounded-lg shadow-lg mb-6">
        <div className="flex items-center space-x-4 mb-6">
          <h2 className="text-3xl font-bold">What Makes This Algo Different?</h2>
        </div>
        <ul className="list-disc pl-6 text-lg mb-4">
          <li><strong className="text-white">Directional trading only</strong>. No short options. No premium-selling strategies.</li>
          <li><strong className="text-white">AI-Driven logic</strong>. Trades are driven by market forecasting, options flow, and crowd behavior, not lagging indicators.</li>
          <li><strong className="text-white">Fully automated execution</strong>. No alerts. No signals to chase. The algo trades for you inside your account.</li>
          <li><strong className="text-white">You stay in control</strong>. You can pause, stop, or manually override any trade at any time.</li>
        </ul>
      </div>

      {/* Section 7: Built for Real Traders */}
      <div className="p-6 bg-gradient-to-br from-blue-500 to-indigo-700 text-white rounded-lg shadow-lg">
        <div className="flex items-center space-x-4 mb-6">
          <h2 className="text-3xl font-bold">Built for Real Traders. Backed by Real Results.</h2>
        </div>
        <p className="text-lg">
          Quantum Flo isn’t theory, it’s the next step in democratizing sophisticated, hedge fund-level trading technology. With its intelligent entry and exit engine, strict cash requirements, pattern day trader compliance, and seamless integration with top brokers, the QF Auto Algo gives you the confidence and control you need to trade smarter, faster, and without second-guessing.
        </p>
        <p className="text-lg">
          <strong className="text-white">Let the machine do the work while you stay in charge.</strong>
        </p>
      </div>
    </>
  );
};

export default BotOverview;
